import type { Body, BodyUUID, Geom, Material, Mesh, WorldBody } from "../../types/scene";
import { create } from "mutative";
import { createDefaultPhongMaterial } from "../../utils/materialDefaults";
import { generateUuid } from "../../utils/id";
import { logger } from "../../utils/logger";
import type { SceneStoreSetState } from "../sceneStore.types";
import type { BodyActions } from "./actionTypes";
import { withHistory } from "./withHistory";
import { generateUniqueWorldBodyName } from "./bodyActionUtils";

export const createBodyMeshActions = (
  set: SceneStoreSetState
): Pick<BodyActions, "importMeshes"> => ({
  // Import multiple meshes as root bodies in one history-tracked action
  importMeshes: withHistory(
    (
      meshUuids: string[],
      options?: { collisionMeshUuids?: string[]; bothMeshUuids?: string[] }
    ) => {
      set((state) =>
        create(state, (draft) => {
          if (!Array.isArray(meshUuids) || meshUuids.length === 0) {
            return;
          }
          const collisionMeshUuids = new Set((options?.collisionMeshUuids || []).map(String));
          const bothMeshUuids = new Set((options?.bothMeshUuids || []).map(String));

          const existingDefault = Object.entries(draft.materials).find(
            ([, mat]: [string, Material]) => mat?.name === "default_mat"
          );
          const defaultMaterialId = existingDefault
            ? existingDefault[0]
            : (() => {
                const generated = {
                  ...createDefaultPhongMaterial("default_mat", {
                    color: [0.75, 0.75, 0.75, 1],
                  }),
                  autoGenerated: true,
                } as Material & { autoGenerated: boolean };
                draft.materials[generated.uuid] = generated;
                return generated.uuid;
              })();

          // Add each mesh as a new body directly to the flat map
          meshUuids.forEach((meshUuid: string) => {
            const bodyUuid = generateUuid() as BodyUUID;
            const meshMeta = draft.meshes?.[meshUuid as keyof typeof draft.meshes] as
              | Mesh
              | undefined;
            const meshName = meshMeta?.name || meshUuid;
            const useBoth = bothMeshUuids.has(String(meshUuid));
            const visualGeomUuid = generateUuid();
            const collisionGeomUuid = generateUuid();
            const makeMeshGeom = (visualOnly: boolean, uuid = generateUuid()) =>
              ({
                uuid,
                kind: "mesh",
                mesh: meshUuid,
                materials: visualOnly ? [defaultMaterialId] : [],
                position: [0, 0, 0],
                orientation: [1, 0, 0, 0],
                size: [1, 1, 1],
                visualOnly,
              }) as Geom;
            const newBody: Body = {
              uuid: bodyUuid,
              name: meshName,
              position: [0, 0, 0],
              orientation: [1, 0, 0, 0],
              geoms: useBoth
                ? [
                    {
                      ...makeMeshGeom(true, visualGeomUuid),
                      pairedGeomUUID: collisionGeomUuid,
                    },
                    {
                      ...makeMeshGeom(false, collisionGeomUuid),
                      pairedGeomUUID: visualGeomUuid,
                    },
                  ]
                : [makeMeshGeom(!collisionMeshUuids.has(String(meshUuid)))],
              children: [],
              sites: [],
            } as Body;

            // Add to flat bodies map
            draft.bodies[bodyUuid] = newBody;
            // Add to worldBodies (root level)
            // Always generate "Object 1", "Object 2", etc. for robot-level world body labels
            if (!draft.worldBodies) draft.worldBodies = [];
            const worldBodyName = generateUniqueWorldBodyName(draft.worldBodies as WorldBody[]);
            draft.worldBodies.push({ uuid: bodyUuid, name: worldBodyName });
          });

          try {
            window.dispatchEvent(new CustomEvent("structure-changed"));
          } catch (error) {
            logger.warn(
              "[bodyActions] Failed to dispatch structure-changed after importMeshes",
              error
            );
          }
        })
      );
    }
  ),
});
